import { Link } from 'react-router-dom';
import type { Project } from '../../../mocks/projects';

interface ProjectHeroProps {
  project: Project;
}

const sectionLabels: Record<string, string> = {
  research: 'Research & Curatorial Projects',
  portfolio: 'Portfolio',
  archives: 'Archives',
};

const sectionAnchors: Record<string, string> = {
  research: '/#research',
  portfolio: '/#portfolio',
  archives: '/#archives',
};

export default function ProjectHero({ project }: ProjectHeroProps) {
  return (
    <div className="relative w-full">
      {/* Hero image — full natural height, no cropping */}
      <div className="w-full relative overflow-hidden">
        <img
          src={project.image}
          alt={`${project.title} — curated by Oludamola Adebowale, Nigerian cultural historian`}
          title={project.title}
          className="w-full h-auto block"
          style={{ display: 'block' }}
        />
        {/* Top gradient for breadcrumb readability — hidden on mobile */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-transparent hidden md:block" />
        {/* Bottom gradient for title readability — hidden on mobile */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent hidden md:block" />

        {/* Breadcrumb — hidden on mobile */}
        <div className="absolute top-8 left-0 right-0 max-w-5xl mx-auto px-6 lg:px-12 hidden md:block">
          <nav className="flex items-center gap-2 text-white/80 text-sm font-sans">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-white/50" />
            <a href={sectionAnchors[project.section]} className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">
              {sectionLabels[project.section]}
            </a>
            <i className="ri-arrow-right-s-line text-white/50" />
            <span className="text-white/60 truncate max-w-xs">{project.title}</span>
          </nav>
        </div>

        {/* Title overlay — hidden on mobile */}
        <div className="absolute bottom-0 left-0 right-0 max-w-5xl mx-auto px-6 lg:px-12 pb-12 hidden md:block">
          <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-3 font-sans">
            {project.category}
          </p>
          <h1 className="font-serif text-3xl lg:text-5xl font-bold text-white leading-tight max-w-3xl">
            {project.title}
          </h1>
          {(project.year || project.venue) && (
            <p className="font-sans text-sm text-white/70 mt-4">
              {project.year && <span>{project.year}</span>}
              {project.year && project.venue && <span className="mx-2">·</span>}
              {project.venue && <span>{project.venue}</span>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}